import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./TeamPage.css";
import backgroundImage from "url:../../public/hand.png";

function Team() {
  const navigate = useNavigate();
  const [expandedMember, setExpandedMember] = useState(null);
  const [visibleMembers, setVisibleMembers] = useState([]);
  const [activeTab, setActiveTab] = useState({});
  const [hoveredMember, setHoveredMember] = useState(null);
  const memberRefs = useRef([]);

  useEffect(() => {
    document.body.style.backgroundImage = `url(${backgroundImage})`;
    document.body.style.backgroundSize = "cover";
    document.body.style.backgroundRepeat = "no-repeat";
    document.body.style.backgroundAttachment = "fixed";

    // Stagger animation for team members
    const observers = [];

    memberRefs.current.forEach((member, index) => {
      if (!member) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setTimeout(() => {
                setVisibleMembers((prev) => [...new Set([...prev, index])]);
              }, index * 300);
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.2,
        },
      );

      observer.observe(member);
      observers.push(observer);
    });

    return () => {
      document.body.style.backgroundImage = null;
      document.body.style.backgroundSize = null;
      document.body.style.backgroundRepeat = null;
      document.body.style.backgroundAttachment = null;
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  const teamMembers = [
    {
      name: "CHARBEL MATTA",
      title: "Founder / Managing Partner",
      initials: "CM",
      color: "#667eea",
      certifications: [
        "Lebanese Certified Public Accountant (LACPA) and Sworn Court Expert",
      ],
      education: [
        {
          degree: "Bachelor of Business Administration in Accounting",
          institution: "Pigier, Beirut, Lebanon",
        },
        {
          degree: "Informatique de Gestion",
          institution:
            "Institut Universitaire de Technologie, Orsay, Paris, France",
        },
      ],
      experience: [
        {
          position: "Founder/Managing Partner",
          company: "Matta Financial Services",
          location: "Byblos, Lebanon",
          years: "2007 - Present",
        },
        {
          position: "Senior Auditor",
          company: "Sabeh Khalil Accounting and Auditing",
          location: "Beirut, Lebanon",
          years: "1986-2006",
        },
        {
          position: "Auditor",
          company: "Antoine Daher Accounting and Auditing",
          location: "Beirut, Lebanon",
          years: "1984-1985",
        },
      ],
      stats: {
        experience: "35+ Years",
        projects: "500+",
        clients: "60+",
      },
    },
    {
      name: "JOE MATTA",
      title: "Manager",
      initials: "JM",
      color: "#764ba2",
      certifications: [
        "US Certified Public Accountant (US CPA)",
        "Lebanese Certified Public Accountant (LACPA)",
      ],
      education: [
        {
          degree: "Bachelor of Business Administration in Accounting",
          institution: "Lebanese American University (LAU), Byblos, Lebanon",
        },
      ],
      experience: [
        {
          position: "Manager",
          company: "Matta Financial Services",
          location: "Byblos, Lebanon",
          years: "2023 - Present",
        },
        {
          position: "Senior Auditor",
          company: "Ernst & Young (EY)",
          location: "Beirut, Lebanon",
          years: "2019-2023",
        },
      ],
      stats: {
        experience: "6+ Years",
        projects: "200+",
        clients: "40+",
      },
    },
  ];

  const toggleExpand = (index) => {
    setExpandedMember(expandedMember === index ? null : index);
    if (expandedMember !== index) {
      setActiveTab((prev) => ({ ...prev, [index]: "certifications" }));
    }
  };

  const handleTabChange = (memberIndex, tab) => {
    setActiveTab((prev) => ({ ...prev, [memberIndex]: tab }));
  };

  const handleContact = (e) => {
    e.stopPropagation();
    navigate("/contact-information");
  };

  return (
    <div className="team-page-container">
      <div className="team-header">
        <h1 className="team-title">Meet Our Team</h1>
        <p className="team-subtitle">
          Experienced professionals dedicated to your success
        </p>
        <div className="team-stats-banner">
          <div className="team-stat">
            <span className="team-stat-value">{teamMembers.length}</span>
            <span className="team-stat-label">Team Members</span>
          </div>
          <div className="team-stat-divider"></div>
          <div className="team-stat">
            <span className="team-stat-value">40+</span>
            <span className="team-stat-label">Years Combined Experience</span>
          </div>
          <div className="team-stat-divider"></div>
          <div className="team-stat">
            <span className="team-stat-value">4+</span>
            <span className="team-stat-label">Certifications</span>
          </div>
        </div>
      </div>

      <div className="team-members-grid">
        {teamMembers.map((member, index) => (
          <div
            key={index}
            ref={(el) => (memberRefs.current[index] = el)}
            className={`member-container ${
              visibleMembers.includes(index) ? "visible" : ""
            } ${expandedMember === index ? "expanded" : ""} ${
              hoveredMember === index ? "hovered" : ""
            }`}
            onMouseEnter={() => setHoveredMember(index)}
            onMouseLeave={() => setHoveredMember(null)}
          >
            <div className="member-header" onClick={() => toggleExpand(index)}>
              <div className="member-avatar-section">
                <div
                  className="member-avatar"
                  style={{
                    background: `linear-gradient(135deg, ${member.color}, ${member.color}dd)`,
                  }}
                >
                  <span className="avatar-initials">{member.initials}</span>
                </div>
                <div className="member-name-title-container">
                  <div className="member-name">
                    <strong>{member.name}</strong>
                  </div>
                  <div className="member-title">{member.title}</div>
                </div>
              </div>
              <button className="expand-btn">
                {expandedMember === index ? "−" : "+"}
              </button>
            </div>

            {/* Stats Preview */}
            <div className="member-stats-preview">
              <div className="stat-preview-item">
                <span className="stat-preview-icon">📅</span>
                <span className="stat-preview-value">
                  {member.stats.experience}
                </span>
                <span className="stat-preview-label">Experience</span>
              </div>
              <div className="stat-preview-item">
                <span className="stat-preview-icon">📊</span>
                <span className="stat-preview-value">
                  {member.stats.projects}
                </span>
                <span className="stat-preview-label">Projects</span>
              </div>
              <div className="stat-preview-item">
                <span className="stat-preview-icon">🤝</span>
                <span className="stat-preview-value">
                  {member.stats.clients}
                </span>
                <span className="stat-preview-label">Clients</span>
              </div>
            </div>

            <div
              className={`member-info ${
                expandedMember === index ? "show" : ""
              }`}
            >
              {/* Tab Navigation */}
              <div className="info-tabs">
                <button
                  className={`tab-btn ${
                    activeTab[index] === "certifications" || !activeTab[index]
                      ? "active"
                      : ""
                  }`}
                  onClick={() => handleTabChange(index, "certifications")}
                >
                  <span className="tab-icon">🎓</span>
                  Certifications
                </button>
                <button
                  className={`tab-btn ${
                    activeTab[index] === "education" ? "active" : ""
                  }`}
                  onClick={() => handleTabChange(index, "education")}
                >
                  <span className="tab-icon">📚</span>
                  Education
                </button>
                <button
                  className={`tab-btn ${
                    activeTab[index] === "experience" ? "active" : ""
                  }`}
                  onClick={() => handleTabChange(index, "experience")}
                >
                  <span className="tab-icon">💼</span>
                  Experience
                </button>
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {(activeTab[index] === "certifications" ||
                  !activeTab[index]) && (
                  <div className="info-section">
                    <ul>
                      {member.certifications.map((cert, i) => (
                        <li
                          key={i}
                          className="fade-in-item"
                          style={{ animationDelay: `${i * 0.1}s` }}
                        >
                          <span className="list-icon">✓</span>
                          {cert}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {activeTab[index] === "education" && (
                  <div className="info-section">
                    <ul>
                      {member.education.map((edu, i) => (
                        <li
                          key={i}
                          className="fade-in-item education-item"
                          style={{ animationDelay: `${i * 0.1}s` }}
                        >
                          <div className="education-degree">
                            <span className="list-icon">🎓</span>
                            <strong>{edu.degree}</strong>
                          </div>
                          <span className="institution">{edu.institution}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {activeTab[index] === "experience" && (
                  <div className="info-section">
                    <div className="timeline-experience">
                      {member.experience.map((exp, i) => (
                        <div
                          key={i}
                          className="fade-in-item experience-item"
                          style={{ animationDelay: `${i * 0.1}s` }}
                        >
                          <div className="experience-timeline-dot"></div>
                          <div className="experience-content">
                            <div className="experience-header">
                              <strong className="experience-position">
                                {exp.position}
                              </strong>
                              {exp.years && (
                                <span className="experience-years">
                                  {exp.years}
                                </span>
                              )}
                            </div>
                            <div className="experience-company">
                              {exp.company}
                            </div>
                            <span className="experience-location">
                              {exp.location}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="member-actions">
                <button
                  className="action-btn contact-btn"
                  onClick={handleContact}
                >
                  <span className="btn-icon">✉️</span>
                  Contact {member.name.split(" ")[0]}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="team-cta-section">
        <h2 className="cta-heading">Want to Work With Our Team?</h2>
        <p className="cta-text">
          Get in touch with us to discuss how we can help your business grow
        </p>
        <button
          className="cta-main-btn"
          onClick={() => navigate("/contact-information")}
        >
          Contact Us Today →
        </button>
      </div>
    </div>
  );
}

export default Team;
